import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import CTASection from "@/components/CTASection";
import HowItWorks from "@/components/HowItWorks";
import {
  CATEGORY_BY_SLUG,
  KITCHEN_CONFIGURATIONS,
  KITCHEN_MATERIALS,
  KITCHEN_GALLERY,
  RENDER_VS_REALITY,
} from "@/lib/siteData";

const KitchenPage = () => {
  const category = CATEGORY_BY_SLUG["kitchen"];

  return (
    <div data-testid="kitchen-page">
      {/* HERO */}
      <section className="relative">
        <div className="relative h-[80vh] min-h-[560px] overflow-hidden">
          <img src={category.hero} alt="Modular kitchens built for Indian homes" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/65 via-black/30 to-transparent" />
          <div className="absolute inset-0 flex items-end">
            <div className="max-w-7xl mx-auto w-full px-5 md:px-10 pb-14 md:pb-24">
              <div className="max-w-3xl text-white">
                <div className="inline-block bg-black/50 backdrop-blur-sm px-3 py-1.5 mb-5">
                  <div className="overline" style={{ color: "#F8D16F" }}>Modular kitchens</div>
                </div>
                <h1 className="font-display text-5xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tight font-light">
                  Modular Kitchens<br />
                  <span className="italic">Built for Indian Homes.</span>
                </h1>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* OVERVIEW */}
      <section className="py-20 md:py-28 bg-ivory">
        <div className="max-w-7xl mx-auto px-5 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5 reveal">
            <div className="overline mb-4">Overview</div>
            <h2 className="font-display text-4xl md:text-5xl leading-[1.05] tracking-tight font-light">
              Designed for how<br />
              <span className="italic text-teal">India actually cooks.</span>
            </h2>
          </div>
          <div className="lg:col-span-7 reveal space-y-5 text-lg md:text-xl text-black/75 leading-relaxed">
            <p>
              Every Tarush kitchen is designed around Indian usage patterns — the daily rhythm of chopping, tempering, pressure-cooking, and cleaning. Storage is optimised for utensils, groceries, and appliances that Indian households actually own.
            </p>
            <p>
              Built with durability in mind — carcasses that survive humidity, finishes that resist stains, and hardware rated for heavy daily use.
            </p>
          </div>
        </div>
      </section>

      {/* CONFIGURATIONS */}
      <section data-testid="kitchen-configurations" className="bg-cream py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14 md:mb-16 reveal">
            <div className="max-w-2xl">
              <div className="overline mb-4">Configuration types</div>
              <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight font-light">
                Five layouts,<br />
                <span className="italic text-navy">infinite possibilities.</span>
              </h2>
            </div>
            <p className="md:text-right md:max-w-sm text-black/60">
              Every project starts by picking the right footprint — we plan the modules, storage, and finishes around it.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {KITCHEN_CONFIGURATIONS.map((c, i) => (
              <div key={c.name} data-testid={`config-${c.name.toLowerCase()}`} className="reveal group" style={{ transitionDelay: `${i * 60}ms` }}>
                <div className="aspect-[4/3] overflow-hidden bg-ivory border border-brandgray flex items-center justify-center p-8">
                  <svg viewBox="0 0 100 75" className="w-full h-full" aria-hidden="true">
                    <rect x="1" y="1" width="98" height="73" fill="none" stroke="#E3DED2" strokeWidth="0.4" strokeDasharray="1 1" />
                    {c.diagram.map((r, ri) => (
                      <rect
                        key={ri}
                        x={r.x}
                        y={r.y}
                        width={r.w}
                        height={r.h}
                        fill="#294890"
                        opacity={ri === c.diagram.length - 1 && c.name === "Island" ? "0.75" : "1"}
                      />
                    ))}
                  </svg>
                </div>
                <div className="mt-5">
                  <div className="font-display text-2xl md:text-[26px] leading-tight text-navy">{c.name}</div>
                  <p className="mt-2 text-sm md:text-base text-black/70 leading-relaxed">{c.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MATERIALS & FINISHES */}
      <section data-testid="kitchen-materials" className="bg-ivory py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <div className="max-w-3xl mb-12 md:mb-16 reveal">
            <div className="overline mb-4">Materials &amp; finishes</div>
            <h2 className="font-display text-4xl md:text-5xl leading-[1.05] tracking-tight font-light">
              Choice of specifications<br />
              <span className="italic text-navy">as per project and budget.</span>
            </h2>
            <p className="mt-6 text-base md:text-lg text-black/70 leading-relaxed">
              Every kitchen can be built up or down in specification. Below are the primary options — we advise on the right combination during your dealer consultation.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {/* Boards */}
            <div className="reveal bg-white border-t-4 border-teal p-8 md:p-10">
              <div className="overline mb-3">Board options</div>
              <div className="font-display text-2xl md:text-3xl leading-tight tracking-tight text-navy">Carcass ply</div>
              <ul className="mt-5 space-y-3 text-base text-black/80">
                {KITCHEN_MATERIALS.boards.map((b) => (
                  <li key={b} className="flex gap-3 items-center border-t border-brandgray pt-3">
                    <span className="w-1.5 h-1.5 bg-teal shrink-0" />
                    <span className="font-medium">{b}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Finishes */}
            <div className="reveal bg-white border-t-4 border-coral p-8 md:p-10" style={{ transitionDelay: "80ms" }}>
              <div className="overline mb-3">Shutter finishes</div>
              <div className="font-display text-2xl md:text-3xl leading-tight tracking-tight text-navy">Laminate &amp; Acrylic</div>
              <ul className="mt-5 space-y-3 text-base text-black/80">
                {KITCHEN_MATERIALS.finishes.map((f) => (
                  <li key={f.name} className="border-t border-brandgray pt-3">
                    <div className="font-medium">{f.name}</div>
                    <div className="text-sm text-teal mt-0.5">{f.specs}</div>
                  </li>
                ))}
              </ul>
            </div>

            {/* Hardware */}
            <div className="reveal bg-white border-t-4 border-gold p-8 md:p-10" style={{ transitionDelay: "160ms" }}>
              <div className="overline mb-3">Hardware brands</div>
              <div className="font-display text-2xl md:text-3xl leading-tight tracking-tight text-navy">Soft-close, standard</div>
              <ul className="mt-5 grid grid-cols-2 gap-2 text-base text-black/80">
                {KITCHEN_MATERIALS.hardware.map((h) => (
                  <li key={h} className="border-t border-brandgray pt-3 font-medium">{h}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* GALLERY STRIP */}
      <section data-testid="kitchen-gallery-strip" className="bg-cream py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <div className="mb-10 md:mb-14 reveal">
            <div className="max-w-2xl">
              <div className="overline mb-4">Recent kitchens</div>
              <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight font-light">
                Installed &amp; <span className="italic text-navy">living.</span>
              </h2>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            {KITCHEN_GALLERY.map((src, i) => (
              <div key={i} className="reveal aspect-[4/5] overflow-hidden bg-ivory" style={{ transitionDelay: `${i * 40}ms` }}>
                <img src={src} alt={`Kitchen project ${i + 1}`} loading="lazy" className="w-full h-full object-cover hover:scale-105 transition-transform duration-[900ms]" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* RENDER VS REALITY */}
      <section data-testid="render-vs-reality" className="bg-ivory py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12 md:mb-16 reveal">
            <div className="max-w-2xl">
              <div className="overline mb-4">Render vs. reality</div>
              <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight font-light">
                What you're shown.<br />
                <span className="italic text-navy">What we deliver.</span>
              </h2>
            </div>
            <p className="md:text-right md:max-w-sm text-black/60">
              Side-by-side comparisons of the 3D concept and the installed kitchen — because a render only counts if the reality matches.
            </p>
          </div>

          <div className="space-y-10 md:space-y-14">
            {RENDER_VS_REALITY.map((pair, i) => (
              <div key={pair.label} className="reveal" style={{ transitionDelay: `${i * 80}ms` }}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-4">
                  <figure className="relative">
                    <div className="aspect-[4/3] overflow-hidden bg-cream">
                      <img src={pair.render} alt={`${pair.label} — render`} loading="lazy" className="w-full h-full object-cover" />
                    </div>
                    <figcaption className="absolute top-4 left-4">
                      <span className="inline-block bg-black/65 text-white text-[10px] md:text-xs uppercase tracking-[0.28em] px-3 py-1.5 backdrop-blur-sm">Render</span>
                    </figcaption>
                  </figure>
                  <figure className="relative">
                    <div className="aspect-[4/3] overflow-hidden bg-cream">
                      <img src={pair.reality} alt={`${pair.label} — reality`} loading="lazy" className="w-full h-full object-cover" />
                    </div>
                    <figcaption className="absolute top-4 left-4">
                      <span className="inline-block bg-black/65 text-gold text-[10px] md:text-xs uppercase tracking-[0.28em] px-3 py-1.5 backdrop-blur-sm">Reality</span>
                    </figcaption>
                  </figure>
                </div>
                <div className="mt-4 flex items-center gap-3 text-sm text-black/60">
                  <span className="inline-block w-8 h-px bg-teal" />
                  <span>{pair.label} — from 3D concept to installed reality.</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS (customer journey) */}
      <HowItWorks
        heading="How it works"
        subheading="From a showroom visit to a finished kitchen — five clean steps."
      />

      <CTASection
        eyebrow="Ready to design yours?"
        title="Get a Quotation."
        body="Share your floor plan or requirement and our team will respond with a scope, timeline, and indicative pricing."
        primaryLabel="Get a Quotation"
        primaryTo="/contact"
        secondaryLabel="Become a Dealer"
        secondaryTo="/become-a-dealer"
        testid="kitchen-cta"
      />
    </div>
  );
};

export default KitchenPage;
